import * as React from "react";

import { Tooltip } from "@/shared/foundation/primitives";

import styles from "./ExplainedControl.module.css";

/**
 * A control with a tooltip saying what it does or why it is disabled. The
 * tooltip opens on hover and on focus, and its text is also the control's
 * accessible description: pass `describedBy` to the control's
 * `aria-describedby`.
 *
 * Pass the control's `disabled` too. A disabled control can't take focus, so
 * the wrapper takes it instead; an enabled one keeps the only tab stop.
 */
export function ExplainedControl({
  explanation,
  disabled,
  children,
}: {
  explanation: string;
  disabled: boolean;
  children: (describedBy: string) => React.ReactNode;
}) {
  const id = React.useId();
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={250}
        render={
          <span
            tabIndex={disabled ? 0 : undefined}
            className={styles.trigger}
          />
        }
      >
        {children(id)}
        <span id={id} className="sr-only">
          {explanation}
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content>{explanation}</Tooltip.Content>
    </Tooltip>
  );
}
